import React, { useState, useEffect } from "react"
import { useStaticQuery, graphql, Link } from "gatsby"
import styled from "styled-components"
import "react-input-range/lib/css/index.css"
import { BodyCopy, StyledHeading, CallToAction } from "../../common"

const PricingTableWrapper = styled.div`
  background: #f3f3f3;
  padding: 4rem 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  > div:nth-child(1) {
    text-align: center;
  }
  .cta {
    display: flex;
    justify-content: center;
  }
  @media (min-width: 768px) {
    padding: calc(20px + 160 * (100vw - 320px) / 2180) 3rem;
  }
  @media ${(props) => props.theme.breakpoints.medium} {
    padding: 4rem 2rem 4rem;
  }
`

const PricingTableInner = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 3rem;
  margin: 4rem auto 0.5rem auto;
  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.5rem;
    margin: 4rem auto;
  }
  @media ${(props) => props.theme.breakpoints.xl} {
    grid-template-columns: repeat(4, 1fr);
    gap: 2rem;
    margin: 4rem auto;
  }
`

const StyledPricingTableItem = styled.div`
  display: flex;
  box-shadow: ${(props) =>
    props.$isPopular ? "4px 4px 12px 0px rgba(0, 0, 0, 0.11)" : 0};
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  background: #fff;
  border-radius: 1.2rem;
  margin-bottom: 3rem;
  position: relative;
  header {
    border-radius: 10px 10px 0px 0px;
    overflow: hidden;
    position: relative;
    text-align: center;
    background: ${(props) => props.color};
    padding: 1rem 0;
    h2 {
      font-weight: 500;
      color: #fff;
      margin: 0;
    }
  }
  .is-popular {
    position: absolute;
    top: -45px;
    z-index: 20;
    padding: 10px 32px;
    border: 2px solid ${(props) => props.color};
    border-radius: 9px;
    background-color: #fff;
  }
  ${BodyCopy} {
    padding: 2rem;
    color: #000;
  }
  section {
    padding: 2rem 3rem;
    border-bottom: 2px solid #e5e5e5;
    text-align: center;
    > span {
      display: flex;
      justify-content: center;
      margin-bottom: 2rem;
      color: #b6b6b6;
      font-size: 16px;
    }
    h3 {
      text-align: center;
      color: ${(props) => props.color};
      font-size: 18px;
    }
    h1 {
      color: #000;
      margin-top: 4rem;
      font-size: 65px;
      line-height: 1;
      text-align: center;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
  @media (min-width: 768px) {
    width: 100%;
    margin-bottom: 5rem;
  }
`

const PricingTableItem = ({
  title1,
  sub_heading,
  description,
  package_color,
  base_price,
  monthly_price,
  is_popular,
  is_poa,
  discount,
  link,
  linkText,
  link_target,
}) => {
  const discountPrice = base_price * discount
  const actualPrice = base_price - discountPrice
  const roundedPrice = Number(actualPrice?.toFixed())
  const buttonText = linkText?.text?.replace(/\s+/g, "").toLowerCase()

  const comingSoonButtonText = buttonText === "comingsoon" ? true : false

  return (
    <StyledPricingTableItem color={package_color} $isPopular={is_popular}>
      {is_popular && (
        <StyledHeading
          size="h3"
          as="h3"
          color={package_color}
          className="is-popular"
        >
          Most Popular
        </StyledHeading>
      )}
      <div style={{ width: "100%" }}>
        <header>
          <StyledHeading size="h2">{title1?.text}</StyledHeading>
        </header>
        <section>
          <StyledHeading size="h3">{sub_heading?.text}</StyledHeading>
          <StyledHeading size="h1" as="h1" weight={600}>
            <span>{is_poa === "false" ? `$${roundedPrice}` : "POA"}</span>
          </StyledHeading>
          <span>PER FARM PER YEAR</span>
          {monthly_price?.text && (
            <StyledHeading weight={600} size="h4">
              <span>{monthly_price?.text}</span>
            </StyledHeading>
          )}
        </section>
        <BodyCopy dangerouslySetInnerHTML={{ __html: description?.html }} />
      </div>
      <div>
        {linkText && (
          <Link
            to={
              link_target
                ? `${link?.url}#${link_target}`
                : link?.url || "/signup"
            }
            className={`${
              comingSoonButtonText ? "styled-button-reversed" : "styled-button"
            }`}
            style={{
              cursor: comingSoonButtonText ? "not-allowed" : "pointer",
              color: comingSoonButtonText ? package_color : "#ffffff",
              border: `2px solid ${
                comingSoonButtonText ? package_color : "#ffffff"
              }`,
              background: comingSoonButtonText ? "#ffffff" : package_color,
              margin: "0 0 2rem 0",
            }}
            state={{ enquiryType: title1?.text }}
          >
            {linkText?.text}
          </Link>
        )}
      </div>
    </StyledPricingTableItem>
  )
}

function inRange(x, min, max) {
  return (x - min) * (x - max) <= 0
}

export const PricingTable = ({
  items,
  heading,
  text,
  anchor_id,
  link_type,
  link_text,
  link_target,
  page_link,
}) => {
  const [farms] = useState(1)
  const [discount, setDiscount] = useState(0)
  const { prismicDiscounts } = useStaticQuery(graphql`
    query discountQuery {
      prismicDiscounts {
        data {
          table_discounts {
            discount_percentage
            farms_high
            farms_low
          }
        }
      }
    }
  `)

  useEffect(() => {
    const { table_discounts } = prismicDiscounts?.data

    const discountTier = table_discounts?.filter((item) => {
      const low = Number(item?.farms_low)
      const high = Number(item?.farms_high)
      return inRange(farms, low, high)
    })
    const discount = discountTier?.length
      ? discountTier[0]?.discount_percentage
      : 0

    setDiscount(discount / 100)
  }, [farms, prismicDiscounts])

  return (
    <PricingTableWrapper id={anchor_id}>
      <div>
        <StyledHeading size="h2">{heading?.text}</StyledHeading>
        <BodyCopy
          dangerouslySetInnerHTML={{ __html: text?.html }}
          $measureLarge
        />
      </div>
      <PricingTableInner>
        {items?.map((item, index) => (
          <PricingTableItem
            key={`pricingTableItem-${index}`}
            {...item}
            discount={discount}
          />
        ))}
      </PricingTableInner>
      {link_text && (
        <div className="cta">
          <CallToAction
            type={link_type}
            text={link_text}
            target={link_target}
            page={page_link}
          />
        </div>
      )}
    </PricingTableWrapper>
  )
}
